import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle, Briefcase, ChevronRight, HelpCircle, Loader2,
  MessageSquare, Plus, RefreshCw, Send, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

/* ─── shared badge configs ───────────────────────────────────────────────────── */
const STATUS_STYLES = {
  open:        'bg-blue-500/10 text-blue-600 border-blue-500/20',
  in_progress: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  resolved:    'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
  closed:      'bg-slate-500/10 text-slate-500 border-slate-400/20',
};
const STATUS_LABELS   = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const PRIORITY_STYLES = {
  low:    'bg-slate-500/10 text-slate-500',
  medium: 'bg-amber-500/10 text-amber-700',
  high:   'bg-orange-500/10 text-orange-700',
  urgent: 'bg-red-500/10 text-red-600',
};
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

const CATEGORY_LABELS = {
  interview_access: 'Interview Access', technical_issue: 'Technical',
  scheduling: 'Scheduling', account: 'Account', billing: 'Billing', general: 'General',
};

const ADMIN_CATEGORIES = [
  { value: 'billing',          label: 'Billing / Subscription' },
  { value: 'technical_issue',  label: 'Technical Issue' },
  { value: 'account',          label: 'Account / Access' },
  { value: 'general',          label: 'General Query' },
];
const PRIORITIES = [
  { value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
];
const STATUS_OPTIONS = ['open', 'in_progress', 'resolved', 'closed'];

/* ─── TicketRow ──────────────────────────────────────────────────────────────── */
function TicketRow({ ticket, onClick }) {
  const raisedBy = ticket.raisedBy;
  const name = raisedBy
    ? `${raisedBy.firstName || ''} ${raisedBy.lastName || ''}`.trim() || raisedBy.email
    : '—';

  return (
    <button
      onClick={() => onClick(ticket._id)}
      className="w-full text-left px-4 py-3.5 hover:bg-muted/40 transition-colors flex items-start gap-3 border-b border-border last:border-0"
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 mt-0.5">
        <MessageSquare className="h-4 w-4 text-primary" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', STATUS_STYLES[ticket.status])}>
            {STATUS_LABELS[ticket.status]}
          </span>
          <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', PRIORITY_STYLES[ticket.priority])}>
            {PRIORITY_LABELS[ticket.priority]}
          </span>
          <span className="text-[10px] text-muted-foreground font-mono">{ticket.ticketNumber}</span>
        </div>
        <p className="text-sm font-medium text-foreground truncate">{ticket.subject}</p>
        <p className="text-[11px] text-muted-foreground">
          {ticket.type === 'candidate_to_company' ? (
            <><span className="font-medium">{name}</span> · {CATEGORY_LABELS[ticket.category] || ticket.category}</>
          ) : (
            <>{CATEGORY_LABELS[ticket.category] || ticket.category}</>
          )}
          {' · '}
          {ticket.lastActivityAt ? new Date(ticket.lastActivityAt).toLocaleDateString() : ''}
        </p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-2.5" />
    </button>
  );
}

/* ─── TicketDetailSheet ──────────────────────────────────────────────────────── */
function TicketDetailSheet({ ticketId, open, onClose, onUpdated, allowInternal = false }) {
  const { user } = useAuth();
  const [ticket,  setTicket]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply,   setReply]   = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const res = await apiClient.getSupportTicketById(ticketId);
      setTicket(res.data);
    } catch { toast.error('Could not load ticket.'); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleSend = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await apiClient.addTicketMessage(ticket._id, { content: reply.trim(), isInternal });
      setReply(''); setIsInternal(false);
      await load();
      toast.success('Reply sent.');
    } catch (err) { toast.error(err.message || 'Failed to send reply.'); }
    finally { setSending(false); }
  };

  const handleStatus = async (status) => {
    setUpdatingStatus(true);
    try {
      await apiClient.updateTicketStatus(ticket._id, status);
      await load();
      onUpdated?.();
      toast.success(`Ticket marked as ${STATUS_LABELS[status]}.`);
    } catch (err) { toast.error(err.message || 'Failed to update status.'); }
    finally { setUpdatingStatus(false); }
  };

  const currentUserId = user?._id;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) { onClose(); setTicket(null); } }}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl" side="right">
        {/* Header */}
        <SheetHeader className="shrink-0 border-b border-border px-4 py-3 space-y-2">
          {loading ? (
            <Skeleton className="h-5 w-48" />
          ) : ticket ? (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-semibold', STATUS_STYLES[ticket.status])}>
                      {STATUS_LABELS[ticket.status]}
                    </span>
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', PRIORITY_STYLES[ticket.priority])}>
                      {PRIORITY_LABELS[ticket.priority]}
                    </span>
                    <span className="text-[10px] text-muted-foreground font-mono">{ticket.ticketNumber}</span>
                  </div>
                  <SheetTitle className="text-sm leading-snug">{ticket.subject}</SheetTitle>
                  {ticket.sessionTitle && (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />{ticket.sessionTitle}
                    </p>
                  )}
                  {ticket.raisedBy && (
                    <p className="text-[11px] text-muted-foreground">
                      Raised by: <span className="font-medium text-foreground">
                        {`${ticket.raisedBy.firstName || ''} ${ticket.raisedBy.lastName || ''}`.trim() || ticket.raisedBy.email}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* Status actions */}
              {ticket.status !== 'closed' && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {STATUS_OPTIONS.filter(s => s !== ticket.status).map(s => (
                    <button
                      key={s}
                      onClick={() => handleStatus(s)}
                      disabled={updatingStatus}
                      className={cn(
                        'rounded-full border px-2.5 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80',
                        STATUS_STYLES[s]
                      )}
                    >
                      {updatingStatus ? '…' : `Mark ${STATUS_LABELS[s]}`}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </SheetHeader>

        {/* Thread */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
          {!loading && ticket?.messages?.map((msg, i) => {
            if (msg.isInternal && allowInternal) {
              return (
                <div key={i} className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-xs">
                  <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">🔒 Internal Note · {msg.senderName}</p>
                  <p className="text-foreground leading-relaxed">{msg.content}</p>
                </div>
              );
            }
            if (msg.isInternal) return null;
            const isOwn = msg.senderId === currentUserId || msg.senderId?._id === currentUserId;
            return (
              <div key={i} className={cn('flex items-end gap-2', isOwn ? 'flex-row-reverse' : 'flex-row')}>
                <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                  isOwn ? 'bg-primary/15 text-primary' : 'bg-emerald-500/15 text-emerald-700')}>
                  {msg.senderName?.slice(0,2).toUpperCase() || '?'}
                </div>
                <div className={cn('max-w-[70%] space-y-1', isOwn && 'items-end flex flex-col')}>
                  {!isOwn && <p className="text-[10px] text-muted-foreground ml-1">{msg.senderName}</p>}
                  <div className={cn('rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                    isOwn ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm')}>
                    {msg.content}
                  </div>
                  <p className={cn('text-[10px] text-muted-foreground', isOwn ? 'text-right' : 'text-left')}>
                    {msg.createdAt ? new Date(msg.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply */}
        <div className="shrink-0 border-t border-border bg-card px-4 py-3 space-y-2">
          {ticket?.status === 'closed' ? (
            <p className="text-center text-xs text-muted-foreground py-1">This ticket is closed.</p>
          ) : (
            <>
              {allowInternal && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                  <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)}
                    className="rounded border-border" />
                  Internal note (hidden from candidate)
                </label>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Type your reply… (Enter to send)"
                  rows={2}
                  disabled={sending}
                  className={cn(
                    'flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm',
                    'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary',
                    isInternal && 'border-amber-500/40 bg-amber-500/5'
                  )}
                />
                <Button size="icon" onClick={handleSend} disabled={!reply.trim() || sending} className="h-9 w-9 shrink-0 rounded-xl">
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ─── RaiseAdminTicketModal ──────────────────────────────────────────────────── */
function RaiseAdminTicketModal({ open, onClose, onCreated }) {
  const [category,    setCategory]    = useState('general');
  const [priority,    setPriority]    = useState('medium');
  const [subject,     setSubject]     = useState('');
  const [description, setDescription] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  const reset = () => { setCategory('general'); setPriority('medium'); setSubject(''); setDescription(''); setError(''); };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!subject.trim()) return setError('Please enter a subject.');
    if (description.trim().length < 20) return setError('Description must be at least 20 characters.');
    setSubmitting(true);
    try {
      await apiClient.createSupportTicket({ type: 'company_to_superadmin', category, priority, subject: subject.trim(), description: description.trim() });
      toast.success('Ticket raised! EchoHire support will respond shortly.');
      onCreated?.();
      handleClose();
    } catch (err) { setError(err.message || 'Failed to raise ticket.'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <HelpCircle className="h-4 w-4 text-primary" />
            </div>
            Contact EchoHire Support
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">Raise a ticket to the EchoHire platform support team.</p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                {ADMIN_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Subject <span className="text-destructive">*</span></label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} maxLength={200}
              placeholder="Brief summary of your issue"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Description <span className="text-destructive">*</span></label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} maxLength={4000}
              placeholder="Describe your issue in detail…"
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
            <p className="text-right text-[10px] text-muted-foreground">{description.length}/4000</p>
          </div>
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" size="sm" disabled={submitting} className="gap-1.5">
              {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Submitting…</> : <><Send className="h-3.5 w-3.5" />Submit Ticket</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─── TicketList ─────────────────────────────────────────────────────────────── */
function TicketList({ tickets, loading, onSelect, emptyText }) {
  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-md" />)}</div>;
  if (tickets.length === 0) return (
    <div className="rounded-lg border border-border bg-card px-6 py-10 text-center">
      <MessageSquare className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
      <p className="text-sm text-muted-foreground">{emptyText}</p>
    </div>
  );
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card shadow-sm">
      {tickets.map(t => <TicketRow key={t._id} ticket={t} onClick={onSelect} />)}
    </div>
  );
}

/* ─── AdminSupportPage ───────────────────────────────────────────────────────── */
export default function AdminSupportPage() {
  const [activeTab,       setActiveTab]       = useState('candidate');
  const [statusFilter,    setStatusFilter]    = useState('');
  const [candidateTickets, setCandidateTickets] = useState([]);
  const [myTickets,       setMyTickets]       = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showRaiseModal,  setShowRaiseModal]  = useState(false);
  const [stats,           setStats]           = useState({ open: 0, in_progress: 0, resolved: 0, closed: 0, total: 0 });

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, mRes, sRes] = await Promise.all([
        apiClient.getSupportTickets({ type: 'candidate_to_company', status: statusFilter || undefined }),
        apiClient.getSupportTickets({ type: 'company_to_superadmin', status: statusFilter || undefined }),
        apiClient.getSupportTicketStats(),
      ]);
      setCandidateTickets(cRes.data || []);
      setMyTickets(mRes.data || []);
      setStats(sRes.data || {});
    } catch { toast.error('Could not load tickets.'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const displayTickets = activeTab === 'candidate' ? candidateTickets : myTickets;

  return (
    <div className="space-y-5 p-2">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Support</h1>
          <p className="text-sm text-muted-foreground">Manage candidate issues and contact EchoHire support</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadTickets} className="gap-1.5 h-8">
            <RefreshCw className="h-3.5 w-3.5" />Refresh
          </Button>
          {activeTab === 'my' && (
            <Button size="sm" onClick={() => setShowRaiseModal(true)} className="gap-1.5 h-8">
              <Plus className="h-3.5 w-3.5" />New Ticket
            </Button>
          )}
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Open',        key: 'open',        color: 'text-blue-600'    },
          { label: 'In Progress', key: 'in_progress', color: 'text-amber-600'   },
          { label: 'Resolved',    key: 'resolved',    color: 'text-emerald-600' },
          { label: 'Closed',      key: 'closed',      color: 'text-slate-500'   },
        ].map(({ label, key, color }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
            className={cn(
              'rounded-xl border border-border bg-card p-4 text-left transition-all hover:shadow-sm',
              statusFilter === key && 'ring-2 ring-primary/40 border-primary/30'
            )}
          >
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={cn('text-2xl font-bold mt-0.5', color)}>{stats[key] ?? 0}</p>
          </button>
        ))}
      </div>

      {/* Tab switcher */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-9">
          <TabsTrigger value="candidate" className="text-xs gap-1.5">
            Candidate Tickets
            {candidateTickets.filter(t => t.status === 'open').length > 0 && (
              <span className="rounded-full bg-primary/15 text-primary px-1.5 py-0.5 text-[9px] font-bold">
                {candidateTickets.filter(t => t.status === 'open').length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="my" className="text-xs">My Tickets to EchoHire</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {['', 'open', 'in_progress', 'resolved', 'closed'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-all',
              statusFilter === s
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-foreground/30'
            )}
          >
            {s ? STATUS_LABELS[s] : 'All'}
          </button>
        ))}
      </div>

      {/* Ticket list */}
      <TicketList
        tickets={displayTickets}
        loading={loading}
        onSelect={setSelectedTicketId}
        emptyText={activeTab === 'candidate'
          ? 'No candidate tickets found.'
          : 'No tickets raised to EchoHire support yet.'}
      />

      {/* Detail sheet */}
      <TicketDetailSheet
        ticketId={selectedTicketId}
        open={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        onUpdated={loadTickets}
        allowInternal={activeTab === 'candidate'}
      />

      {/* Raise ticket modal (admin → super admin) */}
      <RaiseAdminTicketModal
        open={showRaiseModal}
        onClose={() => setShowRaiseModal(false)}
        onCreated={loadTickets}
      />
    </div>
  );
}
